import i18next from "i18next";
import { initReactI18next } from "react-i18next";

import { useUiStore } from "@stores/uiStore";

import {
  defaultNamespace,
  namespaces,
  resolveI18nLanguage,
  supportedLanguages,
} from "./settings";

import type { BackendModule, ReadCallback, ResourceKey } from "i18next";

type ILanguageResources = Record<string, unknown>;
interface ILocaleModule {
  default: ILanguageResources;
}

const localeModules = import.meta.glob<ILocaleModule>("../locales/*/index.ts");
const languageResourcePromises = new Map<string, Promise<ILanguageResources>>();

function loadLanguageResources(language: string): Promise<ILanguageResources> {
  const modulePath = `../locales/${language}/index.ts`;
  const loadLocaleModule = localeModules[modulePath];

  if (!loadLocaleModule) {
    return Promise.reject(new Error(`Missing i18n language: ${modulePath}`));
  }

  const existingPromise = languageResourcePromises.get(language);

  if (existingPromise) {
    return existingPromise;
  }

  const languageResourcePromise = loadLocaleModule().then(
    (localeModule) => localeModule.default,
  );

  languageResourcePromises.set(language, languageResourcePromise);

  return languageResourcePromise;
}

const tsLocaleBackend: BackendModule = {
  type: "backend",
  init: () => {},
  read(language: string, namespace: string, callback: ReadCallback) {
    loadLanguageResources(language)
      .then((languageResources) => {
        const resource = languageResources[namespace];

        if (!resource || typeof resource !== "object") {
          callback(
            new Error(`Missing i18n namespace: ${language}/${namespace}`),
            null,
          );
          return;
        }

        callback(null, resource as ResourceKey);
      })
      .catch((error: unknown) => {
        callback(error instanceof Error ? error : String(error), null);
      });
  },
};

const initialLanguage = resolveI18nLanguage(useUiStore.getState().locale);

if (useUiStore.getState().locale !== initialLanguage) {
  useUiStore.getState().changeLocale(initialLanguage);
}

i18next
  .use(tsLocaleBackend)
  .use(initReactI18next)
  .init({
    lng: initialLanguage,
    fallbackLng: false,
    supportedLngs: [...supportedLanguages],
    load: "currentOnly",
    ns: [...namespaces],
    defaultNS: defaultNamespace,
    interpolation: { escapeValue: false },
    returnNull: false,
    react: {
      useSuspense: true,
    },
  });

useUiStore.subscribe(
  (state) => state.locale,
  (locale) => i18next.changeLanguage(resolveI18nLanguage(locale)),
);
